/**
 * App-wide "you are clocked in" bar.
 *
 * A technician clocks in from one visit's panel and then spends the shift on
 * other screens — the calendar, their checklist, a photo upload — where nothing
 * says the clock is still running. Forgetting to clock out is a payroll
 * correction someone has to make by hand, so the running punch follows the user
 * across every page instead of living only on the visit that started it.
 *
 * Rendered once, in Layout, directly under the Header (a sibling of the header
 * bar, not inside it — the header is `h-14 overflow-hidden`). Full-width and
 * wrapping, so it reads the same on a phone as on a desktop.
 *
 * ⚠️ Deliberately NOT gated on `role === "technician"`. Clocking in is gated on
 * being *assigned to the visit*, not on a role (see _visit_for_clock), so a
 * dispatch or PM user working a visit can be clocked in too — and would then be
 * the one person with no reminder. The banner renders for whoever has a running
 * punch, which is inherently only people who clocked themselves in.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useAuth } from "../context/AuthContext";
import { fetchMyOpenClock } from "../api/visits";
import { formatProperty } from "../utils/property";

const fmtClock = (iso) => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat("h:mm a") : null;
};

function fmtElapsed(fromIso, nowMs) {
  const start = DateTime.fromISO(fromIso);
  if (!start.isValid) return null;
  const mins = Math.max(0, Math.floor((nowMs - start.toMillis()) / 60000));
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
}

export default function ClockedInBanner() {
  const { isAuthed, me } = useAuth();
  const [now, setNow] = useState(() => Date.now());

  // One small indexed query. Polled because the punch can also be closed from
  // another device (the tech's phone) or by a management correction.
  const { data: open } = useQuery({
    queryKey: ["myOpenClock"],
    queryFn: fetchMyOpenClock,
    enabled: Boolean(isAuthed && me?.uid),
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

  // Tick only while the elapsed time is actually on screen.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [open]);

  if (!open) return null;

  const site = formatProperty(open.property_number, open.property_city);
  const where = [open.customer_name, site].filter(Boolean).join(" · ");
  const started = fmtClock(open.clock_in);
  const elapsed = fmtElapsed(open.clock_in, now);

  // What still stands between this person and clocking out. The visit's own
  // panel enforces and explains both — this only says how many.
  const tasksLeft = open.outstanding_checklist_items ?? 0;
  const needsSheet = open.requires_safety_sheet && !open.has_safety_sheet;
  // Last-work-day Technician Closure tasks (owner ask, Oct 7 2026) — only ever
  // non-zero on the job's last work day; see outstanding_tech_closure_items_for.
  const closureLeft = open.outstanding_tech_closure_items ?? 0;
  const blocker = needsSheet
    ? "Safety sheet not submitted"
    : tasksLeft > 0
      ? `${tasksLeft} checklist task${tasksLeft === 1 ? "" : "s"} left`
      : closureLeft > 0
        ? `${closureLeft} last-day task${closureLeft === 1 ? "" : "s"} left`
        : null;

  return (
    <div
      role="status"
      className="shrink-0 bg-success-50 border-b border-success-200 px-4 py-2
                 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
    >
      <span className="flex items-center gap-2 font-medium text-success-800">
        <span className="w-2 h-2 rounded-full bg-success-600 animate-pulse" aria-hidden="true" />
        Clocked in
      </span>

      <span className="text-success-800">
        {where || `Visit #${open.visit_number ?? open.visit_id}`}
        {open.visit_number && where ? ` · Visit #${open.visit_number}` : ""}
      </span>

      <span className="text-success-700 text-xs">
        {started ? `since ${started}` : ""}
        {elapsed ? `${started ? " · " : ""}${elapsed}` : ""}
      </span>

      {blocker && <span className="text-warning-700 text-xs">{blocker}</span>}

      {/* The clock-out button itself stays on the visit, where the safety sheet
          and checklist that gate it also live — a one-click clock out from here
          would fail with an explanation the user can't act on from here.
          ⚠️ ?visitId= is what makes the link actually land on the running
          visit instead of a job page with every row collapsed (and the clock
          control unmounted) — see JobPage.jsx's deep-link effect. Same
          ?xId=-and-strip convention as Estimates.jsx/PurchaseOrders.jsx. */}
      {open.job_id && (
        <Link
          to={`/jobs/${open.job_id}?visitId=${open.visit_id}`}
          className="ml-auto text-xs font-medium text-success-800 underline
                     underline-offset-2 hover:text-success-900"
        >
          Open visit
        </Link>
      )}
    </div>
  );
}
